import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

export class DateConversionUtils {
  // 01/01/2025 -> JS Date object
  static shorthandToDate(shorthand: string): Date | null {
    if (!DateConversionUtils.isValidShorthandDate(shorthand)) {
      return null;
    }
    const [day, month, year] = shorthand.split('/').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  }

  // NgbDate -> JS Date object
  static ngbDateToDate(ngbDate: NgbDate): Date | null {
    if (!ngbDate || !ngbDate.year || !ngbDate.month || !ngbDate.day) {
      return null;
    }
    return new Date(Date.UTC(ngbDate.year, ngbDate.month - 1, ngbDate.day, 0, 0, 0, 0));
  }

  // JS Date -> 01/01/2025
  static dateToShorthand(date: Date): string {
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  // JS Date -> 2025-01-01 (DateOnly wire format - no time component)
  static dateToNativeInput(date: Date): string {
    const year = date.getUTCFullYear().toString().padStart(4, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // JS Date -> January 1, 2025
  static dateToLongform(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  }

  // JS Date -> Jan 1, 2025
  static dateToAbbreviated(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  }

  // JS Date -> NgbDate
  static dateToNgbDate(date: Date): NgbDate {
    return new NgbDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }

  /**
   * Extracts year/month/day directly out of a DateOnly (yyyy-MM-dd) wire string, without ever
   * constructing a `Date` object from it. Native `Date` parsing of a bare date-only string is
   * always UTC per spec, so reading local components (getFullYear/getMonth/getDate) back off of
   * that would silently shift the day for any non-zero-offset timezone. Use this whenever doing
   * date-math (age calculation, year extraction, etc.) against a DateOnly value.
   * No end anchor on the regex, so this also safely handles a stray full-ISO string by reading
   * just its leading date portion.
   */
  static parseDateOnlyParts(dateOnly: string): { year: number; month: number; day: number } | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOnly);
    if (!match) {
      return null;
    }
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  }

  /**
   * True when a DateOnly (yyyy-MM-dd) wire value falls on or before `asOf` (defaults to today) in the
   * user's local timezone. Compares calendar parts instead of `Date` instances, because native parsing
   * of a bare date-only string is always UTC and would shift the day for any non-zero-offset timezone.
   */
  static dateOnlyHasArrived(dateOnly: string, asOf: Date = new Date()): boolean {
    const parts = DateConversionUtils.parseDateOnlyParts(dateOnly);
    if (!parts) {
      return false;
    }
    const value = parts.year * 10000 + parts.month * 100 + parts.day;
    const today = asOf.getFullYear() * 10000 + (asOf.getMonth() + 1) * 100 + asOf.getDate();
    return value <= today;
  }

  // Age in whole years as of `asOf` (defaults to now), computed from a DateOnly (yyyy-MM-dd) birth date.
  static calculateAge(dateOfBirth: string, asOf: Date = new Date()): number {
    const dob = DateConversionUtils.parseDateOnlyParts(dateOfBirth);
    if (!dob) {
      return NaN;
    }
    let age = asOf.getFullYear() - dob.year;
    const monthDiff = asOf.getMonth() - (dob.month - 1);
    if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.day)) {
      age--;
    }
    return age;
  }

  static calculateAgeOnJanuaryFirst(dateOfBirth: string, year: number): number {
    return DateConversionUtils.calculateAge(dateOfBirth, new Date(year, 0, 1));
  }

  static isValidShorthandDate(shorthand: string) {
    const dateAsString = shorthand?.toString();
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateAsString?.match(dateRegex);
    if (!match) {
      return false;
    }
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (year < 1) {
      return false;
    }
    if (month < 1 || month > 12) {
      return false;
    }
    const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) {
      daysInMonth[2] = 29;
    }
    return day > 0 && day <= daysInMonth[month];
  }
}
